import { useMemo } from 'react';
import { cn } from '@/app/components/ui/utils';
import {
  Shield,
  AlertTriangle,
  Users,
  Package,
  WrenchIcon,
  Truck,
  Clock,
  ExternalLink,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  XOctagon,
  Info,
  X,
} from 'lucide-react';
import type { BoardProject, BoardTech, BoardAssignment } from '@/hooks/use-staffing-board';

// ── Types ────────────────────────────────────────────────────────────────────

interface ComplianceEvidencePanelProps {
  projects: BoardProject[];
  techs: BoardTech[];
  assignments: BoardAssignment[];
  safetyEnabled: boolean;
  inventoryEnabled: boolean;
  crewEnabled: boolean;
  onOpenDispatchHistory: () => void;
  onClose: () => void;
}

interface ProjectEvidence {
  project: BoardProject;
  assignedTechs: BoardTech[];
  jsaStatus: 'ok' | 'warning' | 'missing';
  l3Coverage: { satisfied: number; required: number } | null;
  expiredCertTechs: BoardTech[];
  nearMissCount: number;
  incidentCount: number;
  gearInspectionDue: number;
  gearFailedCount: number;
  gearLifeExceededCount: number;
  gearDeployedCount: number;
  gearRequestPending: number;
  // Yes/no flags. A caller without H3Safe or H3Gear access gets these with
  // null counts (``*Hidden``), so rows show the warning without a number.
  safetyHidden: boolean;
  gearHidden: boolean;
  hasNearMiss: boolean;
  hasIncident: boolean;
  hasGearFailed: boolean;
  hasGearLifeExceeded: boolean;
  hasGearInspectionDue: boolean;
  hasGearRequestPending: boolean;
  // Requirement hidden from this caller but not met.
  hasHiddenUnmetStaffing: boolean;
  lastDispatchedAt: string | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatRelativeTime(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

function StatusDot({ color }: { color: 'green' | 'amber' | 'red' | 'gray' }) {
  return (
    <span
      className={cn(
        'inline-block h-2 w-2 rounded-full flex-shrink-0',
        color === 'green' && 'bg-green-500',
        color === 'amber' && 'bg-amber-500',
        color === 'red' && 'bg-red-500',
        color === 'gray' && 'bg-gray-400',
      )}
    />
  );
}

// ── Evidence row ─────────────────────────────────────────────────────────────

function EvidenceRow({
  icon: Icon,
  label,
  status,
  detail,
  href,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  status: 'green' | 'amber' | 'red' | 'gray';
  detail: React.ReactNode;
  href?: string;
  onClick?: () => void;
}) {
  const content = (
    <div className="flex items-start gap-2 py-1.5 px-2 rounded hover:bg-gray-50 transition-colors group">
      <Icon className="h-3.5 w-3.5 text-gray-500 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <StatusDot color={status} />
          <span className="text-[11px] font-medium text-gray-700 truncate">{label}</span>
        </div>
        <span className="text-[10px] text-gray-500 block truncate">{detail}</span>
      </div>
      {(href || onClick) && (
        <ExternalLink className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5 flex-shrink-0" />
      )}
    </div>
  );

  if (href) {
    return (
      <a href={href} className="block" onClick={(e) => e.stopPropagation()}>
        {content}
      </a>
    );
  }
  if (onClick) {
    return (
      <button type="button" className="block w-full text-left" onClick={onClick}>
        {content}
      </button>
    );
  }
  return content;
}

// ── Project evidence card ────────────────────────────────────────────────────

function ProjectEvidenceCard({
  evidence,
  safetyEnabled,
  inventoryEnabled,
  crewEnabled,
  onOpenDispatchHistory,
}: {
  evidence: ProjectEvidence;
  safetyEnabled: boolean;
  inventoryEnabled: boolean;
  crewEnabled: boolean;
  onOpenDispatchHistory: () => void;
}) {
  const { project } = evidence;

  const overallStatus: 'green' | 'amber' | 'red' = useMemo(() => {
    if (evidence.jsaStatus === 'missing' || evidence.expiredCertTechs.length > 0 || evidence.hasIncident || evidence.hasGearFailed || evidence.hasGearLifeExceeded) {
      return 'red';
    }
    if (
      evidence.jsaStatus === 'warning' ||
      evidence.hasNearMiss ||
      evidence.hasGearInspectionDue ||
      evidence.hasHiddenUnmetStaffing ||
      (evidence.l3Coverage && evidence.l3Coverage.satisfied < evidence.l3Coverage.required)
    ) {
      return 'amber';
    }
    return 'green';
  }, [evidence]);

  return (
    <div className="border border-gray-200 rounded-lg bg-white" data-testid="compliance-evidence-card">
      {/* Project header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
        <StatusDot color={overallStatus} />
        <a
          href={`/projects/${project.id}`}
          className="text-xs font-semibold text-gray-900 truncate hover:text-blue-600 transition-colors flex-1"
          onClick={(e) => e.stopPropagation()}
        >
          {project.name}
        </a>
        {project.project_number && (
          <span className="text-[10px] text-gray-400">#{project.project_number}</span>
        )}
      </div>

      {/* Evidence rows */}
      <div className="py-1">
        {/* JSA Status */}
        {safetyEnabled && (
          <EvidenceRow
            icon={Shield}
            label="JSA"
            status={evidence.jsaStatus === 'ok' ? 'green' : evidence.jsaStatus === 'warning' ? 'amber' : 'red'}
            detail={
              project.jsa_label
                ? `${project.jsa_label}${project.jsa_signature_count ? ` (${project.jsa_signature_count})` : ''}`
                : evidence.jsaStatus === 'ok'
                  ? `Signed${project.jsa_signature_count ? ` (${project.jsa_signature_count})` : ''}`
                  : evidence.jsaStatus === 'warning'
                    ? `Partially signed (${project.jsa_signature_count ?? 'pending'})`
                    : 'No JSA'
            }
            href={project.jsa_url ?? undefined}
          />
        )}

        {/* L3 Coverage */}
        {crewEnabled && evidence.l3Coverage && (
          <EvidenceRow
            icon={Users}
            label="L3 coverage"
            status={
              evidence.l3Coverage.satisfied >= evidence.l3Coverage.required
                ? 'green'
                : evidence.l3Coverage.satisfied > 0
                  ? 'amber'
                  : 'red'
            }
            detail={`${evidence.l3Coverage.satisfied}/${evidence.l3Coverage.required} L3 assigned`}
            href={`/projects/${project.id}`}
          />
        )}
        {crewEnabled && evidence.hasHiddenUnmetStaffing && (
          <EvidenceRow
            icon={Users}
            label="Staffing"
            status="amber"
            detail="Staffing requirement not met"
          />
        )}

        {/* Expired certs */}
        {crewEnabled && evidence.expiredCertTechs.length > 0 && (
          <EvidenceRow
            icon={ShieldAlert}
            label="Expired certs"
            status="red"
            detail={evidence.expiredCertTechs.map((tech, index) => (
              <span key={tech.id}>
                {index > 0 && ', '}
                {tech.tech_id != null ? (
                  <a
                    href={`/crew/techs/${tech.tech_id}`}
                    className="hover:text-blue-600 hover:underline"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {tech.first_name} {tech.last_name}
                  </a>
                ) : (
                  `${tech.first_name} ${tech.last_name}`
                )}
              </span>
            ))}
          />
        )}
        {crewEnabled && evidence.expiredCertTechs.length === 0 && evidence.assignedTechs.length > 0 && (
          <EvidenceRow
            icon={CheckCircle2}
            label="Certifications"
            status="green"
            detail="All assigned techs current"
          />
        )}

        {/* Incidents / Near misses */}
        {safetyEnabled && (evidence.hasIncident || evidence.hasNearMiss) && (
          <EvidenceRow
            icon={AlertTriangle}
            label="Safety events"
            status={evidence.hasIncident ? 'red' : 'amber'}
            detail={(evidence.safetyHidden
              ? [
                  evidence.hasIncident && 'Open incident',
                  evidence.hasNearMiss && 'Open near miss',
                ]
              : [
                  evidence.incidentCount > 0 && `${evidence.incidentCount} open incident${evidence.incidentCount !== 1 ? 's' : ''}`,
                  evidence.nearMissCount > 0 && `${evidence.nearMissCount} near miss${evidence.nearMissCount !== 1 ? 'es' : ''}`,
                ]
            ).filter(Boolean).join(', ')}
            href={`/projects/${project.id}/safety`}
          />
        )}
        {safetyEnabled && !evidence.hasIncident && !evidence.hasNearMiss && (
          <EvidenceRow
            icon={CheckCircle2}
            label="Safety events"
            status="green"
            detail="No open incidents or near misses"
          />
        )}

        {/* Gear readiness */}
        {inventoryEnabled && (
          <EvidenceRow
            icon={evidence.hasGearFailed ? XOctagon : evidence.hasGearInspectionDue ? WrenchIcon : Package}
            label="Gear"
            status={evidence.hasGearFailed ? 'red' : evidence.hasGearInspectionDue ? 'amber' : evidence.gearDeployedCount > 0 ? 'green' : 'gray'}
            detail={
              evidence.gearHidden
                ? evidence.hasGearFailed
                  ? 'Failed gear — recall required'
                  : evidence.hasGearInspectionDue
                    ? 'Inspections due'
                    : 'No gear warnings'
                : evidence.gearFailedCount > 0
                  ? `${evidence.gearFailedCount} failed — recall required`
                  : evidence.gearInspectionDue > 0
                    ? `${evidence.gearInspectionDue} inspection${evidence.gearInspectionDue !== 1 ? 's' : ''} due`
                    : evidence.gearDeployedCount > 0
                      ? `${evidence.gearDeployedCount} asset${evidence.gearDeployedCount !== 1 ? 's' : ''} deployed`
                      : 'No gear deployed'
            }
            href={project.gear_link ?? undefined}
          />
        )}

        {/* Gear requests */}
        {inventoryEnabled && project.gear_request_summary && (
          <EvidenceRow
            icon={Truck}
            label="Gear requests"
            status={
              (project.gear_request_summary.requested +
                project.gear_request_summary.fulfilled +
                project.gear_request_summary.in_transit +
                (project.gear_request_summary.partially_received ?? 0) +
                (project.gear_request_summary.pending_confirmation ?? 0)) > 0
                ? 'amber'
                : 'green'
            }
            detail={`${project.gear_request_summary.total} total, ${project.gear_request_summary.received} received`}
            href={project.gear_request_link ?? undefined}
          />
        )}
        {inventoryEnabled && !project.gear_request_summary && evidence.gearHidden && evidence.hasGearRequestPending && (
          <EvidenceRow
            icon={Truck}
            label="Gear requests"
            status="amber"
            detail="Requests pending"
          />
        )}

        {/* Dispatch history */}
        {crewEnabled && (
          <EvidenceRow
            icon={Clock}
            label="Last dispatch"
            status={evidence.lastDispatchedAt ? 'green' : 'gray'}
            detail={evidence.lastDispatchedAt ? formatRelativeTime(evidence.lastDispatchedAt) : 'No dispatches yet'}
            onClick={onOpenDispatchHistory}
          />
        )}
      </div>
    </div>
  );
}

// ── Main panel ───────────────────────────────────────────────────────────────

export function ComplianceEvidencePanel({
  projects,
  techs,
  assignments,
  safetyEnabled,
  inventoryEnabled,
  crewEnabled,
  onOpenDispatchHistory,
  onClose,
}: ComplianceEvidencePanelProps) {
  const techMap = useMemo(() => {
    const map = new Map<number, BoardTech>();
    for (const t of techs) {
      map.set(t.id, t);
    }
    return map;
  }, [techs]);

  const evidence: ProjectEvidence[] = useMemo(() => {
    return projects.map((project) => {
      const projectAssignments = assignments.filter((a) => a.project_id === project.id);
      const assignedTechs = projectAssignments
        .map((a) => techMap.get(a.tech_id))
        .filter((t): t is BoardTech => t != null);

      const jsaStatus: 'ok' | 'warning' | 'missing' = safetyEnabled
        ? ((project.jsa_status_color ?? 'red') === 'green'
            ? 'ok'
            : (project.jsa_status_color ?? 'red') === 'yellow'
              ? 'warning'
              : 'missing')
        : 'ok';

      const l3Coverage = project.staffing_requirement && project.staffing_requirement.required_l3 > 0
        ? {
            satisfied: project.staffing_requirement.satisfied_l3,
            required: project.staffing_requirement.required_l3,
          }
        : null;

      const expiredCertTechs = assignedTechs.filter((t) => t.has_expired_certs === true);
      const gearRequestPending = project.gear_request_summary
        ? (project.gear_request_summary.requested ?? 0)
          + (project.gear_request_summary.fulfilled ?? 0)
          + (project.gear_request_summary.in_transit ?? 0)
          + (project.gear_request_summary.partially_received ?? 0)
          + (project.gear_request_summary.pending_confirmation ?? 0)
        : 0;

      return {
        project,
        assignedTechs,
        jsaStatus,
        l3Coverage,
        expiredCertTechs,
        nearMissCount: project.open_near_misses ?? 0,
        incidentCount: project.open_incidents ?? 0,
        gearInspectionDue: project.gear_inspection_due ?? 0,
        gearFailedCount: project.gear_failed_count ?? 0,
        gearLifeExceededCount: project.gear_life_exceeded_count ?? 0,
        gearDeployedCount: project.gear_deployed_count ?? 0,
        gearRequestPending,
        safetyHidden: project.open_near_misses === null || project.open_incidents === null,
        gearHidden: project.gear_failed_count === null,
        hasNearMiss: (project.open_near_misses ?? 0) > 0 || Boolean(project.has_open_near_misses),
        hasIncident: (project.open_incidents ?? 0) > 0 || Boolean(project.has_open_incidents),
        hasGearFailed: (project.gear_failed_count ?? 0) > 0 || Boolean(project.has_gear_failed),
        hasGearLifeExceeded:
          (project.gear_life_exceeded_count ?? 0) > 0 || Boolean(project.has_gear_life_exceeded),
        hasGearInspectionDue:
          (project.gear_inspection_due ?? 0) > 0 || Boolean(project.has_gear_inspection_due),
        hasGearRequestPending: gearRequestPending > 0 || Boolean(project.has_pending_gear_requests),
        hasHiddenUnmetStaffing:
          !project.staffing_requirement && Boolean(project.has_unmet_staffing_requirement),
        lastDispatchedAt: project.last_dispatched_at ?? null,
      };
    });
  }, [projects, assignments, techMap, safetyEnabled]);

  const summary = useMemo(() => {
    let red = 0;
    let amber = 0;
    let green = 0;
    for (const e of evidence) {
      if (e.jsaStatus === 'missing' || e.expiredCertTechs.length > 0 || e.hasIncident || e.hasGearFailed || e.hasGearLifeExceeded) {
        red++;
      } else if (
        e.jsaStatus === 'warning' ||
        e.hasNearMiss ||
        e.hasGearInspectionDue ||
        e.hasGearRequestPending ||
        e.hasHiddenUnmetStaffing ||
        (e.l3Coverage && e.l3Coverage.satisfied < e.l3Coverage.required)
      ) {
        amber++;
      } else {
        green++;
      }
    }
    return { red, amber, green, total: evidence.length };
  }, [evidence]);

  return (
    <div
      className="w-80 flex-shrink-0 border-l border-gray-200 bg-gray-50 flex flex-col h-full print:hidden"
      data-testid="compliance-evidence-panel"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-blue-600" />
            <h2 className="text-sm font-semibold text-gray-900">Compliance Evidence</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close compliance evidence panel"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Summary chips */}
        <div className="flex items-center gap-2 mt-2">
          {summary.red > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
              <XCircle className="h-3 w-3" />
              {summary.red} blocking
            </span>
          )}
          {summary.amber > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20">
              <AlertTriangle className="h-3 w-3" />
              {summary.amber} advisory
            </span>
          )}
          {summary.green > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
              <CheckCircle2 className="h-3 w-3" />
              {summary.green} ready
            </span>
          )}
        </div>
      </div>

      {/* Advisory notice */}
      <div className="mx-3 mt-3 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2" data-testid="compliance-advisory-notice">
        <Info className="h-3.5 w-3.5 text-blue-600 mt-0.5 flex-shrink-0" />
        <p className="text-[10px] text-blue-800 leading-relaxed">
          System-generated readiness summary. A qualified person must review and confirm
          each signal before dispatch.
        </p>
      </div>

      {/* Project evidence list */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {evidence.length === 0 && (
          <div className="text-center py-8">
            <CheckCircle2 className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-xs text-gray-500">No projects to review</p>
          </div>
        )}
        {evidence.map((e) => (
          <ProjectEvidenceCard
            key={e.project.id}
            evidence={e}
            safetyEnabled={safetyEnabled}
            inventoryEnabled={inventoryEnabled}
            crewEnabled={crewEnabled}
            onOpenDispatchHistory={onOpenDispatchHistory}
          />
        ))}
      </div>
    </div>
  );
}
